import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = ["list", "emptyState", "searchInput", "searchResults"]
  static values = { searchUrl: String, saveUrl: String }

  connect() {
    this.sortable = Sortable.create(this.listTarget, {
      handle: "[data-sortable-handle]",
      animation: 150
    })
    this.debounceTimer = null
  }

  disconnect() {
    this.sortable?.destroy()
  }

  onSearch() {
    clearTimeout(this.debounceTimer)
    const query = this.searchInputTarget.value.trim()
    if (query.length < 2) { this.hideResults(); return }
    this.debounceTimer = setTimeout(() => this.fetchResults(query), 300)
  }

  async fetchResults(query) {
    const url = `${this.searchUrlValue}?q=${encodeURIComponent(query)}`
    const response = await fetch(url, { headers: { "Accept": "application/json" } })
    const recipes = await response.json()
    const existingIds = this.currentRecipeIds()

    const filtered = recipes.filter(r => !existingIds.includes(String(r.id)))
    if (filtered.length === 0) { this.hideResults(); return }

    this.searchResultsTarget.innerHTML = filtered.map(r =>
      `<button type="button" class="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm"
              data-action="featured-recipes#addFromResult"
              data-recipe-id="${r.id}" data-recipe-title="${this.escapeHtml(r.title)}"
              data-recipe-pillar="${this.escapeHtml(r.pillar || '')}">${this.escapeHtml(r.title)}
        <span class="text-xs text-gray-400 ml-1">${this.escapeHtml(r.pillar || '')}</span>
      </button>`
    ).join("")
    this.searchResultsTarget.classList.remove("hidden")
  }

  addFromResult(event) {
    const btn = event.currentTarget
    const id = btn.dataset.recipeId
    const title = btn.dataset.recipeTitle
    const pillar = btn.dataset.recipePillar

    const html = `<div class="flex items-center gap-3 p-3 bg-gray-50 rounded-md border border-gray-200"
         data-recipe-id="${id}">
      <span class="cursor-grab text-gray-400" data-sortable-handle><i class="fas fa-grip-vertical"></i></span>
      <div class="flex-1 min-w-0">
        <span class="text-sm font-medium text-gray-900 truncate block">${this.escapeHtml(title)}</span>
        <span class="text-xs text-gray-500">${this.escapeHtml(pillar || "—")}</span>
      </div>
      <button type="button" class="text-gray-400 hover:text-red-500" data-action="featured-recipes#removeRecipe">
        <i class="fas fa-times"></i>
      </button>
    </div>`
    this.listTarget.insertAdjacentHTML("beforeend", html)
    this.hideResults()
    this.searchInputTarget.value = ""
    this.toggleEmptyState()
  }

  removeRecipe(event) {
    event.currentTarget.closest("[data-recipe-id]").remove()
    this.toggleEmptyState()
  }

  async save() {
    const ids = this.currentRecipeIds()
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    await fetch(this.saveUrlValue, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ featured_recipe_ids: ids })
    })
    window.location.reload()
  }

  // --- Helpers ---

  currentRecipeIds() {
    return Array.from(this.listTarget.querySelectorAll("[data-recipe-id]"))
      .map(el => el.dataset.recipeId)
  }

  toggleEmptyState() {
    const hasItems = this.listTarget.querySelectorAll("[data-recipe-id]").length > 0
    this.emptyStateTarget.classList.toggle("hidden", hasItems)
  }

  hideResults() {
    this.searchResultsTarget.classList.add("hidden")
    this.searchResultsTarget.innerHTML = ""
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
